import {useEffect, useMemo, useRef, useState} from "react";

import {RequestState} from "@pg-mono/request-state";

import {USP_LISTING_TILE_CONTROL_VARIANT, USP_LISTING_TILE_TREATMENT_VARIANT} from "../../../ab_testing/usp_listing_tile_ab_test";
import {IStandardMetaData} from "../../../meta_data/reducers/meta_data_reducer";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeOfferViewItemList} from "../../../tracking/amplitude/track_amplitude_offer_view_item_list";
import {IOfferListOffer} from "../../types/IOfferListOffer";
import {useOfferListingUspAnalyticsContext} from "../components/OfferListingUspAnalyticsProvider";
import {isOfferListingRouteQuerySyncedWithLatestQuery} from "../helpers/is_offer_listing_route_query_synced_with_latest_query";
import {IOfferListLatestQuery} from "../types/IOfferListLatestQuery";

type OfferListingRouteQuery = Record<string, string | string[] | null | boolean | undefined>;

interface IUseOfferListingAmplitudeViewItemListTrackingParams {
    offers: IOfferListOffer[];
    amplitudeItemListContext: IAmplitudeItemListContext;
    routeQuery: OfferListingRouteQuery;
    latestQuery: IOfferListLatestQuery;
    requestState: RequestState;
    isInitialOfferListFetchDone: boolean;
    metaDataRevision: IStandardMetaData;
}

const LATE_DATA_FALLBACK_TIMEOUT = 3000;

const getAmplitudeViewItemListContextKey = (itemListContext: IAmplitudeItemListContext, offerIds: string[]) =>
    `${itemListContext.itemListId ?? ""}|${offerIds.join(",")}`;

// DS-A-32
export const useOfferListingAmplitudeViewItemListTracking = (params: IUseOfferListingAmplitudeViewItemListTrackingParams) => {
    const {offers, amplitudeItemListContext, routeQuery, latestQuery, requestState, isInitialOfferListFetchDone, metaDataRevision} = params;
    const uspAnalyticsContext = useOfferListingUspAnalyticsContext();

    const prevTrackedContextKeyRef = useRef<string | null>(null);
    const observedMetaDataRevisionRef = useRef<IStandardMetaData | null>(null);
    const isAwaitingFreshMetaDataRef = useRef(false);
    const lateDataFallbackTimeoutRef = useRef<number | null>(null);
    const hasLateDataFallbackElapsedRef = useRef(false);
    const [lateDataFallbackTick, setLateDataFallbackTick] = useState(0);

    const offerIds = useMemo(() => offers.map(({id}) => id.toString()), [offers]);
    const isListingQuerySyncedWithRoute = isOfferListingRouteQuerySyncedWithLatestQuery(routeQuery, latestQuery);

    const clearLateDataFallbackTimeout = () => {
        if (lateDataFallbackTimeoutRef.current === null) {
            return;
        }

        window.clearTimeout(lateDataFallbackTimeoutRef.current);
        lateDataFallbackTimeoutRef.current = null;
    };

    const resetLateDataFallback = () => {
        clearLateDataFallbackTimeout();
        hasLateDataFallbackElapsedRef.current = false;
    };

    const scheduleLateDataFallback = () => {
        if (lateDataFallbackTimeoutRef.current !== null) {
            return;
        }

        lateDataFallbackTimeoutRef.current = window.setTimeout(() => {
            lateDataFallbackTimeoutRef.current = null;
            hasLateDataFallbackElapsedRef.current = true;
            setLateDataFallbackTick((tick) => tick + 1);
        }, LATE_DATA_FALLBACK_TIMEOUT);
    };

    useEffect(() => clearLateDataFallbackTimeout, []);

    useEffect(() => {
        const hasFreshMetaData = observedMetaDataRevisionRef.current !== metaDataRevision;

        if (hasFreshMetaData) {
            observedMetaDataRevisionRef.current = metaDataRevision;
        }

        if (!isInitialOfferListFetchDone) {
            return;
        }

        if (requestState === RequestState.Waiting) {
            prevTrackedContextKeyRef.current = null;
            isAwaitingFreshMetaDataRef.current = true;
            resetLateDataFallback();
            return;
        }

        if (hasFreshMetaData) {
            isAwaitingFreshMetaDataRef.current = false;
        }

        if (requestState !== RequestState.Success || !isListingQuerySyncedWithRoute || !uspAnalyticsContext) {
            return;
        }

        const contextKey = getAmplitudeViewItemListContextKey(amplitudeItemListContext, offerIds);

        if (prevTrackedContextKeyRef.current === contextKey) {
            resetLateDataFallback();
            return;
        }

        const uspVariant = uspAnalyticsContext.variant;
        const hasResolvedUspVariant = uspVariant === USP_LISTING_TILE_CONTROL_VARIANT || uspVariant === USP_LISTING_TILE_TREATMENT_VARIANT;
        const isWaitingForLateData = !hasResolvedUspVariant || isAwaitingFreshMetaDataRef.current;

        if (isWaitingForLateData && !hasLateDataFallbackElapsedRef.current) {
            scheduleLateDataFallback();
            return;
        }

        prevTrackedContextKeyRef.current = contextKey;
        resetLateDataFallback();

        trackAmplitudeOfferViewItemList({
            offers,
            itemListId: amplitudeItemListContext.itemListId,
            itemListName: amplitudeItemListContext.itemListName ?? "",
            isUspVariant: uspVariant === USP_LISTING_TILE_TREATMENT_VARIANT,
            addressContext: uspAnalyticsContext.addressContext,
            location: "listing_page",
            lat: latestQuery.lat,
            lng: latestQuery.lng
        });
    }, [
        isInitialOfferListFetchDone,
        requestState,
        isListingQuerySyncedWithRoute,
        offerIds.join(","),
        amplitudeItemListContext.itemListId,
        amplitudeItemListContext.itemListName,
        metaDataRevision,
        lateDataFallbackTick,
        offers,
        uspAnalyticsContext
    ]);
};
import {useEffect, useMemo, useRef, useState} from "react";

import {RequestState} from "@pg-mono/request-state";

import {USP_LISTING_TILE_CONTROL_VARIANT, USP_LISTING_TILE_TREATMENT_VARIANT} from "../../../ab_testing/usp_listing_tile_ab_test";
import {IStandardMetaData} from "../../../meta_data/reducers/meta_data_reducer";
import {IAmplitudeItemListContext} from "../../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackAmplitudeOfferViewItemList} from "../../../tracking/amplitude/track_amplitude_offer_view_item_list";
import {IOfferListOffer} from "../../types/IOfferListOffer";
import {useOfferListingUspAnalyticsContext} from "../components/OfferListingUspAnalyticsProvider";
import {isOfferListingRouteQuerySyncedWithLatestQuery} from "../helpers/is_offer_listing_route_query_synced_with_latest_query";
import {IOfferListLatestQuery} from "../types/IOfferListLatestQuery";

type OfferListingRouteQuery = Record<string, string | string[] | null | boolean | undefined>;

interface IUseOfferListingAmplitudeViewItemListTrackingParams {
    offers: IOfferListOffer[];
    amplitudeItemListContext: IAmplitudeItemListContext;
    routeQuery: OfferListingRouteQuery;
    latestQuery: IOfferListLatestQuery;
    requestState: RequestState;
    isInitialOfferListFetchDone: boolean;
    metaDataRevision: IStandardMetaData;
}

const LATE_DATA_FALLBACK_TIMEOUT = 3000;

const getAmplitudeViewItemListContextKey = (itemListContext: IAmplitudeItemListContext, offerIds: string[]) =>
    `${itemListContext.itemListId ?? ""}|${offerIds.join(",")}`;

// DS-A-32
export const useOfferListingAmplitudeViewItemListTracking = (params: IUseOfferListingAmplitudeViewItemListTrackingParams) => {
    const {offers, amplitudeItemListContext, routeQuery, latestQuery, requestState, isInitialOfferListFetchDone, metaDataRevision} = params;
    const uspAnalyticsContext = useOfferListingUspAnalyticsContext();

    const prevTrackedContextKeyRef = useRef<string | null>(null);
    const observedMetaDataRevisionRef = useRef<IStandardMetaData | null>(null);
    const isAwaitingFreshMetaDataRef = useRef(false);
    const lateDataFallbackTimeoutRef = useRef<number | null>(null);
    const hasLateDataFallbackElapsedRef = useRef(false);
    const [lateDataFallbackTick, setLateDataFallbackTick] = useState(0);

    const offerIds = useMemo(() => offers.map(({id}) => id.toString()), [offers]);
    const isListingQuerySyncedWithRoute = isOfferListingRouteQuerySyncedWithLatestQuery(routeQuery, latestQuery);

    const clearLateDataFallbackTimeout = () => {
        if (lateDataFallbackTimeoutRef.current === null) {
            return;
        }

        window.clearTimeout(lateDataFallbackTimeoutRef.current);
        lateDataFallbackTimeoutRef.current = null;
    };

    const resetLateDataFallback = () => {
        clearLateDataFallbackTimeout();
        hasLateDataFallbackElapsedRef.current = false;
    };

    const scheduleLateDataFallback = () => {
        if (lateDataFallbackTimeoutRef.current !== null) {
            return;
        }

        lateDataFallbackTimeoutRef.current = window.setTimeout(() => {
            lateDataFallbackTimeoutRef.current = null;
            hasLateDataFallbackElapsedRef.current = true;
            setLateDataFallbackTick((tick) => tick + 1);
        }, LATE_DATA_FALLBACK_TIMEOUT);
    };

    useEffect(() => clearLateDataFallbackTimeout, []);

    useEffect(() => {
        const hasFreshMetaData = observedMetaDataRevisionRef.current !== metaDataRevision;

        if (hasFreshMetaData) {
            observedMetaDataRevisionRef.current = metaDataRevision;
        }

        if (!isInitialOfferListFetchDone) {
            return;
        }

        if (requestState === RequestState.Waiting) {
            prevTrackedContextKeyRef.current = null;
            isAwaitingFreshMetaDataRef.current = true;
            resetLateDataFallback();
            return;
        }

        if (hasFreshMetaData) {
            isAwaitingFreshMetaDataRef.current = false;
        }

        if (requestState !== RequestState.Success || !isListingQuerySyncedWithRoute || !uspAnalyticsContext) {
            return;
        }

        const contextKey = getAmplitudeViewItemListContextKey(amplitudeItemListContext, offerIds);

        if (prevTrackedContextKeyRef.current === contextKey) {
            resetLateDataFallback();
            return;
        }

        const uspVariant = uspAnalyticsContext.variant;
        const hasResolvedUspVariant = uspVariant === USP_LISTING_TILE_CONTROL_VARIANT || uspVariant === USP_LISTING_TILE_TREATMENT_VARIANT;
        const isWaitingForLateData = !hasResolvedUspVariant || isAwaitingFreshMetaDataRef.current;

        if (isWaitingForLateData && !hasLateDataFallbackElapsedRef.current) {
            scheduleLateDataFallback();
            return;
        }

        prevTrackedContextKeyRef.current = contextKey;
        resetLateDataFallback();

        trackAmplitudeOfferViewItemList({
            offers,
            itemListId: amplitudeItemListContext.itemListId,
            itemListName: amplitudeItemListContext.itemListName ?? "",
            isUspVariant: uspVariant === USP_LISTING_TILE_TREATMENT_VARIANT,
            addressContext: uspAnalyticsContext.addressContext,
            location: "listing_page",
            lat: latestQuery.lat,
            lng: latestQuery.lng
        });
    }, [
        isInitialOfferListFetchDone,
        requestState,
        isListingQuerySyncedWithRoute,
        offerIds.join(","),
        amplitudeItemListContext.itemListId,
        amplitudeItemListContext.itemListName,
        metaDataRevision,
        lateDataFallbackTick,
        offers,
        uspAnalyticsContext
    ]);
};
